import type { RootState } from '../index';

/**
 * Auth Selectors
 *
 * Selectors for auth state to determine user type and permissions.
 * Used by route guards and components to enforce access control.
 */

/**
 * Select if user is a System Admin (isSuper === true)
 * @returns true if user is a system admin
 */
export const selectIsSystemAdmin = (state: RootState): boolean => {
  return state.auth.data?.isSuper === true;
};

/**
 * Select if user is a Tenant User (isSuper === false)
 * @returns true if user is a tenant user
 */
export const selectIsTenantUser = (state: RootState): boolean => {
  return state.auth.data?.isSuper === false;
};

/**
 * Select user type based on isSuper field
 * @returns 'SYSTEM_ADMIN' | 'TENANT_USER' | 'UNKNOWN'
 */
export const selectUserType = (state: RootState): 'SYSTEM_ADMIN' | 'TENANT_USER' | 'UNKNOWN' => {
  if (state.auth.data?.isSuper === true) {
    return 'SYSTEM_ADMIN';
  }
  if (state.auth.data?.isSuper === false) {
    return 'TENANT_USER';
  }
  return 'UNKNOWN';
};

/**
 * Select if user has a specific privilege
 * @param privilege - The privilege key to check
 * @returns true if user has the privilege
 */
export const selectHasPrivilege =
  (privilege: string) =>
  (state: RootState): boolean => {
    return state.auth.data?.privileges?.includes(privilege) ?? false;
  };

/**
 * Select if user has all specified privileges
 * @param privileges - Array of privilege keys to check
 * @returns true if user has all privileges
 */
export const selectHasAllPrivileges =
  (privileges: string[]) =>
  (state: RootState): boolean => {
    if (!state.auth.data?.privileges) {
      return false;
    }
    return privileges.every((privilege) => state.auth.data.privileges.includes(privilege));
  };

/**
 * Select if user has any of the specified privileges
 * @param privileges - Array of privilege keys to check
 * @returns true if user has at least one privilege
 */
export const selectHasAnyPrivilege =
  (privileges: string[]) =>
  (state: RootState): boolean => {
    if (!state.auth.data?.privileges) {
      return false;
    }
    return privileges.some((privilege) => state.auth.data.privileges.includes(privilege));
  };

/**
 * Select user's privileges array
 * @returns array of privilege keys
 */
export const selectPrivileges = (state: RootState): string[] => {
  return state.auth.data?.privileges ?? [];
};

/**
 * Select if user is authenticated
 * @returns true if user is logged in
 */
export const selectIsAuthenticated = (state: RootState): boolean => {
  return state.auth.isLoggedIn && !!state.auth.data?.id;
};

/**
 * Select current user data
 * @returns current user object
 */
export const selectCurrentUser = (state: RootState) => {
  return state.auth.data;
};

/**
 * Select auth loading state
 * @returns true if auth is loading
 */
export const selectAuthLoading = (state: RootState): boolean => {
  return state.auth.loading;
};

/**
 * Select auth error state
 * @returns true if auth has error
 */
export const selectAuthError = (state: RootState): boolean => {
  return state.auth.error;
};

/**
 * Select auth error message
 * @returns error message string
 */
export const selectAuthErrorMsg = (state: RootState): string => {
  return state.auth.errorMsg;
};
